import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type CloseTicketSuccessActionData,
  type CreateCommentSuccessActionData,
  type CreateTicketSuccessActionData,
  type GetDetailSuccessActionData,
  type InitSuccessActionData,
  type RateSuccessActionData,
  type SetAddModalVisibleActionData,
  type SetShowingDetailIdActionData,
  type WithItemId
} from "./actions";
import { sortTickets } from "./lib";
import { type MemberHelpdesk } from "./types";

const getNewStateForCloseTicketCreateComment = (
  state: MemberHelpdesk.AppState,
  ticketId: string,
  ticketDetail: MemberHelpdesk.Api.TicketDetail,
  action: ReduxAction<any>
) => {
  let tickets = state.tickets.map((x) => ({
    ...x,
    ticketStatusKey: x.ticketId === ticketId ? ticketDetail.ticketStatusKey : x.ticketStatusKey,
    ticketStatusName: x.ticketId === ticketId ? ticketDetail.ticketStatusName : x.ticketStatusName,
    lastModifiedOnDate: x.ticketId === ticketId ? new Date().toString() : x.lastModifiedOnDate
  }));

  return {
    ...state,
    tickets: sortTickets(tickets),
    detail: {
      ...state.detail,
      [ticketId]: detailReducer(state.detail[ticketId], action)
    }
  };
};

const detailReducer = (
  state: MemberHelpdesk.TicketDetailAppState,
  action: ReduxAction<any>
): MemberHelpdesk.TicketDetailAppState => {
  switch (action.type) {
    case Actions.CreateUserFeedbackBegin: {
      return {
        ...state,
        error: null,
        status: "Commenting"
      };
    }
    case Actions.CreateUserFeedbackFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.CreateUserFeedbackSuccess: {
      const { userTicketFeedbacks } = action.data as RateSuccessActionData;
      return {
        ...state,
        detail: { ...state.detail!, userTicketFeedbacks },
        status: "Ready"
      };
    }

    case Actions.CloseTicketBegin: {
      return {
        ...state,
        error: null,
        status: "Commenting"
      };
    }
    case Actions.CloseTicketFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.CloseTicketSuccess: {
      const { ticketDetail } = action.data as CloseTicketSuccessActionData;

      return {
        ...state,
        detail: { ...state.detail!, ticketDetail: ticketDetail },
        status: "Ready"
      };
    }

    case Actions.CreateCommentBegin: {
      return {
        ...state,
        error: null,
        status: "Commenting"
      };
    }
    case Actions.CreateCommentFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.CreateCommentSuccess: {
      const detail = action.data as CreateCommentSuccessActionData;
      return {
        ...state,
        detail: detail,
        status: "Ready"
      };
    }

    case Actions.GetDetailBegin: {
      return {
        ...state,
        detail: null,
        error: null,
        status: "Loading"
      };
    }
    case Actions.GetDetailFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.GetDetailSuccess: {
      const { detail } = action.data as GetDetailSuccessActionData;
      return {
        ...state,
        detail,
        error: null,
        status: "Ready"
      };
    }
    default:
      return state;
  }
};

const reducer = (state: MemberHelpdesk.AppState = defaultState, action: ReduxAction<any>): MemberHelpdesk.AppState => {
  switch (action.type) {
    case Actions.InitBegin: {
      return {
        ...state,
        status: "Loading"
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: "Failed"
      };
    }
    case Actions.InitSuccess: {
      const data = action.data as InitSuccessActionData;
      return {
        ...state,
        ...data,
        status: "Ready"
      };
    }

    case Actions.CreateUserFeedbackBegin:
    case Actions.CreateUserFeedbackFailure:
    case Actions.CreateUserFeedbackSuccess:
    case Actions.CreateCommentBegin:
    case Actions.CreateCommentFailure:
    case Actions.CloseTicketBegin:
    case Actions.CloseTicketFailure:
    case Actions.GetDetailBegin:
    case Actions.GetDetailFailure: {
      const { ticketId } = action.params as WithItemId;
      return {
        ...state,
        detail: {
          ...state.detail,
          [ticketId]: detailReducer(state.detail[ticketId], action)
        }
      };
    }

    case Actions.CreateCommentSuccess: {
      const { ticketId } = action.params as WithItemId;
      const data = action.data as CreateCommentSuccessActionData;
      return getNewStateForCloseTicketCreateComment(state, ticketId, data.ticketDetail, action);
    }

    case Actions.CloseTicketSuccess: {
      const { ticketId } = action.params as WithItemId;
      const data = action.data as CloseTicketSuccessActionData;
      return getNewStateForCloseTicketCreateComment(state, ticketId, data.ticketDetail, action);
    }

    case Actions.GetDetailSuccess: {
      const { ticketId } = action.params as WithItemId;

      return {
        ...state,
        tickets: state.tickets.map((x) => ({
          ...x,
          contactHasRead: x.ticketId === ticketId ? true : x.contactHasRead
        })),
        detail: {
          ...state.detail,
          [ticketId]: detailReducer(state.detail[ticketId], action)
        }
      };
    }

    case Actions.CreateTicketBegin: {
      return {
        ...state,
        error: null,
        status: "Creating"
      };
    }
    case Actions.CreateTicketFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.CreateTicketSuccess: {
      const { ticket } = action.data as CreateTicketSuccessActionData;

      return {
        ...state,
        tickets: [ticket, ...state.tickets],
        status: "Ready"
      };
    }

    case Actions.SetShowingDetailId: {
      const { ticketId } = action.data as SetShowingDetailIdActionData;
      return {
        ...state,
        showingDetailId: ticketId
      };
    }

    case Actions.SetAddModalVisible: {
      const { isVisible } = action.data as SetAddModalVisibleActionData;
      return {
        ...state,
        newTicket: isVisible
          ? {
              departmentId: "",
              ticketTypeId: "",
              subject: "",
              message: "",
              mediaIds: []
            }
          : null
      };
    }

    case Actions.UpdateUserBegin: {
      return {
        ...state,
        error: null,
        status: "Updating"
      };
    }
    case Actions.UpdateUserFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.UpdateUserSuccess: {
      return {
        ...state,
        guidelines: (state.guidelines ?? []).map((x) => ({ ...x, hasAccepted: true })),
        guidelinesVideoUrl: null,
        status: "Ready"
      };
    }

    case Actions.UpdateViolationBegin: {
      return {
        ...state,
        error: null,
        status: "Updating"
      };
    }
    case Actions.UpdateViolationFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Failed"
      };
    }
    case Actions.UpdateViolationSuccess: {
      const { guidelineViolationId } = action.data as MemberHelpdesk.Api.GuidelineViolation;
      return {
        ...state,
        guidelineViolations: state.guidelineViolations!.filter((x) => x.guidelineViolationId !== guidelineViolationId),
        status: "Ready"
      };
    }
    default:
      return state;
  }
};

export const defaultState: MemberHelpdesk.AppState = {
  canLeaveFeedback: false,
  detail: {},
  error: null,
  guidelines: [],
  guidelinesVideoUrl: "",
  guidelineViolations: null,
  id: "",
  newTicket: null,
  showingDetailId: null,
  status: "None",
  surveyPageUrl: "",
  tickets: [],
  displayHelpdeskGuidelines: false
};
export const previewState: MemberHelpdesk.AppState = {
  ...defaultState,
  status: "Ready"
};
export default wrapReducer({ defaultState, reducer, previewState });
